"use strict";

const EventEmitter = require("events").EventEmitter;
const Renderer = require("./GridColumnRenderer");
const DefaultColumnRendererComponent = require("../column_renderer/default/DefaultColumnComponent");
const GroupingColumnRendererComponent = require("../column_renderer/grouping/GroupingColumnComponent");
const _ = require("lodash");

class GridColumnComponent extends EventEmitter {
    constructor(configOptions) {
        super();

        this.setMaxListeners(0);
        this._initialConfig = _.clone(configOptions);

        this._id = _.get(configOptions, "id", undefined);
        this._width = this.getInitialWidth();
        this._minWidth = 100;
        this._showHeader = _.get(configOptions, "showHeader", true);
        this._label = _.get(configOptions, "label", "");
        this._description = _.get(configOptions, "description", this._label);
        this._resizable = _.get(configOptions, "resizable", true);
        this._sortable = _.get(configOptions, "sortable", true);
        this._reorder = _.get(configOptions, "reorder", true);
        this._sort = _.get(configOptions, "sort", "");
        this._locked = _.get(configOptions, "locked", false);
        this._shown = _.get(configOptions, "shown", true);
        this._columnRendererComponent = _.get(configOptions, "columnRendererComponent");
        this._entityColumn = _.get(configOptions, "entityColumn", false);
        this._context = _.get(configOptions, "context", undefined);
        this._path = _.get(configOptions, "path", undefined);
        this._grid = _.get(configOptions, "grid");
        this._groupable = _.get(configOptions, "groupable", false);
        this._isGroupingColumn = _.get(configOptions, "isGroupingColumn", false);
        this._isGroupOnlyColumn = _.get(configOptions, "isGroupOnlyColumn", false);
        this._isLockedRight = _.get(configOptions, "isLockedRight", false);
        this._isCombinedColumn = _.get(configOptions, "isCombinedColumn", false);

        this._configSaveTimeout = undefined;
        this._configSaveTimeoutDuration = 200;

        this._renderer = new Renderer(this);

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        let prom = this._renderer._shutdown();
        this._renderer = undefined;
        this._initialConfig = undefined;
        this._id = undefined;
        this._width = undefined;
        this._minWidth = undefined;
        this._showHeader = undefined;
        this._label = undefined;
        this._description = undefined;
        this._resizable = undefined;
        this._sortable = undefined;
        this._sort = undefined;
        this._locked = undefined;
        this._shown = undefined;
        this._columnRendererComponent = undefined;
        this._entityColumn = undefined;
        this._path = undefined;
        this._grid = undefined;
        this._groupable = undefined;
        if (!_.isUndefined(this._configSaveTimeout)) {
            clearTimeout(this._configSaveTimeout);
        }
        this._configSaveTimeout = undefined;
        this._configSaveTimeoutDuration = undefined;
        return prom;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _triggerUserSorted(event) {
        this.emit("UserSorted", {
            column: this.getId(),
            sort: this.getSort(),
            event: event
        });
    }

    _triggerUserReordered(newIndex) {
        this.emit("UserReordered", {
            column: this.getId(),
            newIndex: newIndex
        });
    }

    _triggerUserLocked(event) {
        this.emit("UserLocked", {
            column: this.getId(),
            locked: this.getLocked(),
            event: event
        });
    }

    _triggerUserResized(event) {
        this.emit("UserResized", {
            column: this.getId(),
            width: this.getWidth(),
            event: event
        });
    }

    _saveConfiguration() {
        if (!_.isUndefined(this._configSaveTimeout)) {
            clearTimeout(this._configSaveTimeout);
        }
        this._configSaveTimeout = setTimeout(() => {
            let colJSON = {},
                colId = this.getId();
            colJSON[colId] = this.toJSON();
            this._grid.updateColumn(colJSON);
        }, this._configSaveTimeoutDuration);
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    setId(newVal) {
        this._id = newVal;
        this.emit("ID", this._id);
        return this;
    }

    getId() {
        return this._id;
    }

    setWidth(newVal) {
        this._width = newVal;
        this._saveConfiguration();
        this.emit("Width", this._width);
        return this;
    }

    getInitialWidth() {
        return _.get(this._initialConfig, "width", 200);
    }

    getWidth() {
        return this._width;
    }

    getMinWidth() {
        return this._minWidth;
    }

    setShowHeader(newVal) {
        this._showHeader = newVal;
        this._saveConfiguration();
        this.emit("ShowHeader", this._showHeader);
        return this;
    }

    getShowHeader() {
        return this._showHeader;
    }

    setLabel(newVal) {
        this._label = newVal;
        this._saveConfiguration();
        this.emit("Label", this._label);
        return this;
    }

    getLabel() {
        return this._label;
    }

    setDescription(newVal) {
        this._description = newVal;
        this._saveConfiguration();
        this.emit("Description", this._description);
        return this;
    }

    getDescription() {
        return this._description;
    }

    setResizable(newVal) {
        this._resizable = newVal;
        this._saveConfiguration();
        this.emit("Resizable", this._resizable);
        return this;
    }

    getResizable() {
        return this._resizable;
    }

    setSortable(newVal) {
        this._sortable = newVal;
        this._saveConfiguration();
        this.emit("Sortable", this._sortable);
        return this;
    }

    getSortable() {
        return this._sortable;
    }

    setSort(newVal) {
        this._sort = newVal;
        this._saveConfiguration();
        this.emit("Sort", this._sort);
        return this;
    }

    getSort() {
        return this._sort;
    }

    setLocked(newVal) {
        this._locked = newVal;
        this._saveConfiguration();
        this.emit("Locked", this._locked);
        return this;
    }
    getLocked() {
        return this._locked;
    }

    setLockedRight(value) {
        this._isLockedRight = !!value;
        return this;
    }
    getLockedRight() {
        return this._isLockedRight;
    }

    setShown(newVal) {
        this._shown = newVal;
        this._saveConfiguration();
        this.emit("Shown", this._shown);
        return this;
    }

    getShown() {
        return this._shown;
    }

    setColumnRendererComponent(newVal) {
        this._columnRendererComponent = newVal;
        this._saveConfiguration();
        this.emit("ColumnRendererComponent", this._columnRendererComponent);
        return this;
    }

    getColumnRendererComponent() {
        let columnRendererComponent = DefaultColumnRendererComponent;

        if (this.isGroupingColumn) {
            columnRendererComponent = GroupingColumnRendererComponent;
        }

        return this._columnRendererComponent || columnRendererComponent;
    }

    setEntityColumn(newVal) {
        this._entityColumn = newVal;
        this._saveConfiguration();
        return this;
    }

    getEntityColumn() {
        return this._entityColumn;
    }

    setContext(newVal) {
        this._context = newVal;
        this._saveConfiguration();
        return this;
    }

    getContext() {
        return this._context;
    }

    getPath() {
        return this._path;
    }

    getGrid() {
        return this._grid;
    }

    get isGroupingColumn() {
        return this._isGroupingColumn;
    }
    get isGroupOnlyColumn() {
        return this._isGroupOnlyColumn;
    }
    get isLockedRight() {
        return this.getLockedRight();
    }
    get isCombinedColumn() {
        return this._isCombinedColumn;
    }

    getGroupable() {
        return this._groupable;
    }

    toJSON() {
        return {
            width: this.getWidth(),
            showHeader: this.getShowHeader(),
            label: this.getLabel(),
            description: this.getDescription(),
            resizable: this.getResizable(),
            sortable: this.getSortable(),
            sort: this.getSort(),
            locked: this.getLocked(),
            shown: this.getShown(),
            columnRendererComponent: this.getColumnRendererComponent(),
            entityColumn: this.getEntityColumn(),
            groupable: this.getGroupable(),
            path: this.getPath()
        };
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = GridColumnComponent;
